import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import CustomersDropdown from '../../components/CustomersDropdown'
import ServicesDropdown from '../../components/ServicesDropdown'
import CampaignDropdown from '../../components/CampaignDropdown'
import DomainDropdown from '../../components/DomainDropdown'
import AssigneeDropdown from '../../components/AssigneeDropdown'
import { Select } from 'antd'
import dayjs from 'dayjs'
import {
  useCreateReportRequestMutation,
  useGetReportRequestsQuery
} from '../../redux/apis/reports/reportsApiSlice'
import Loader from '../pages/loader'
import { can } from '../../helpers/auth'
import { reportValidationSchema } from './validation/reportValidation'
import { buildReports } from './config/reportsConfig'
import ReportSelector from './reports-fields/ReportSelector'
import DateField from './reports-fields/DateField'
import ReusableTable from '../../components/other/ReuseableTable'
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter
} from '../../components/other/card/card'

const GenerateReports = React.memo(() => {
  const context = useContext(AppSettings)

  const [reportId, setReportId] = useState()
  const [clientName, setClientName] = useState(null)
  const [serviceName, setServiceName] = useState(null)
  const [campaignName, setCampaignName] = useState(null)
  const [domainName, setDomainName] = useState(null)
  const [opsName, setOpsName] = useState(null)
  const [operationType, setOperationType] = useState(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })

  const [createReportRequest, { isLoading }] = useCreateReportRequestMutation()
  const {
    data: reportRequests,
    isLoading: isLoadingRequests,
    isFetching,
    refetch
  } = useGetReportRequestsQuery({
    page: pagination.current,
    page_size: pagination.pageSize
  })
  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }
  const validation = useFormik({
    initialValues: {
      customer: '',
      service: '',
      report_name: '',
      start_date: '',
      end_date: '',
      service_id: '',
      campaign_name: '',
      domain_name: '',
      operation: '',
      ugc_level: '',
      operator_id: ''
    },
    validationSchema: reportValidationSchema,
    onSubmit: async values => {
      const reportsAvailable = buildReports(
        values,
        clientName,
        serviceName,
        campaignName,
        domainName,
        opsName,
        operationType
      )

      const selectedReport = reportsAvailable.find(r => r.value === reportId)

      if (!selectedReport) {
        toast.error('Please select a valid report')
        return
      }

      toast.info('Submitting report request...')
      try {
        await createReportRequest({
          report_type: selectedReport.report_type,
          params: selectedReport.params
        }).unwrap()
        toast.success(
          `Report "${selectedReport.label}" request submitted successfully! Check the Reports List below once it's completed.`
        )
        validation.resetForm()
        setReportId(null)
      } catch (error) {
        toast.error(
          `Failed to submit report request: ${
            error?.data?.message || error?.message || error
          }`
        )
      }
    }
  })

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  // Reset form fields when report selection changes
  useEffect(() => {
    if (reportId) {
      validation.resetForm({
        values: {
          customer: '',
          service: '',
          report_name: validation.values.report_name,
          start_date: '',
          end_date: '',
          service_id: '',
          campaign_name: '',
          domain_name: '',
          operation: '',
          ugc_level: '',
          operator_id: ''
        }
      })
      setClientName(null)
      setServiceName(null)
      setCampaignName(null)
      setDomainName(null)
      setOpsName(null)
      setOperationType(null)
    }
  }, [reportId])

  const reportsAvailable = buildReports(
    validation.values,
    clientName,
    serviceName,
    campaignName,
    domainName,
    opsName,
    operationType
  )

  const reportRequestColumns = [
    {
      title: 'Report Title',
      dataIndex: 'report_title',
      key: 'report_title',
      search: true,
      render: text => <p>{text?.replaceAll('_', ' ') || 'N/A'}</p>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Processing', value: 'processing' },
        { text: 'Completed', value: 'completed' },
        { text: 'Failed', value: 'failed' }
      ],
      render: status => (
        <span
          className={`badge ${
            status?.toLowerCase() === 'completed'
              ? 'bg-success'
              : status?.toLowerCase() === 'failed'
              ? 'bg-danger'
              : status?.toLowerCase() === 'processing'
              ? 'bg-info'
              : 'bg-warning'
          }`}
        >
          {status || 'N/A'}
        </span>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: date => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: 'Download',
      key: 'result_url',
      render: (_, record) =>
        record.result_url && record.status?.toLowerCase() === 'completed' ? (
          <a
            href={record.result_url}
            target='_blank'
            rel='noopener noreferrer'
            className='btn btn-sm btn-primary'
          >
            Download
          </a>
        ) : (
          '-'
        )
    }
  ]

  return (
    <>
      <Card>
        <Loader isLoading={isLoading} />
        <CardHeader>Generate Reports</CardHeader>
        <CardBody>
          <form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            {/* Report selector */}
            <ReportSelector
              reportId={reportId}
              setReportId={setReportId}
              validation={validation}
              reportsAvailable={reportsAvailable}
            />
            {reportId && [8, 9, 12, 15, 18, 19].includes(reportId) === false && (
              <div className='mb-3'>
                <CustomersDropdown
                  onChange={(e, option) => {
                    validation.setFieldValue('customer', e)
                    setClientName(option.label.replaceAll(' ', '_'))
                  }}
                  className='customSelect'
                  value={validation.values.customer}
                  onBlur={validation.handleBlur}
                  isAll={reportId !== 5 && reportId !== 22 ? true : false}
                />
                {validation.touched.customer && validation.errors.customer ? (
                  <p className='text-danger'>{validation.errors.customer}</p>
                ) : null}
              </div>
            )}
            {[1, 2, 3, 4, 5, 6, 11, 12, 16, 21].includes(reportId) && (
              <div className='mb-3'>
                <ServicesDropdown
                  onChange={(e, option) => {
                    validation.setFieldValue('service', e)
                    setServiceName(option.label?.replaceAll(' ', '_'))
                  }}
                  className='customSelect'
                  onBlur={validation.handleBlur}
                  value={validation.values.service}
                  service_type='campaign'
                  isAll
                />
                {validation.touched.service && validation.errors.service ? (
                  <p className='text-danger'>{validation.errors.service}</p>
                ) : null}
              </div>
            )}
            {[5, 11, 12, 16, 17, 21, 24].includes(reportId) && (
              <div className='mb-3'>
                <CampaignDropdown
                  onBlur={validation.handleBlur}
                  onChange={(e, option) => {
                    validation.setFieldValue('campaign_name', e)
                    setCampaignName(option.label.replaceAll(' ', '_'))
                  }}
                  value={validation.values.campaign}
                  activeCampaignsOnly={true}
                  className='customSelect'
                  isAll={[10, 11, 12, 16, 17, 21, 24].includes(reportId)}
                />
                {validation.touched.campaign && validation.errors.campaign ? (
                  <p className='text-danger'>{validation.errors.campaign}</p>
                ) : null}
              </div>
            )}
            {reportId === 24 && (
              <div className='mb-3'>
                <label className='form-label'>UGC Level</label>
                <Select
                  className='customSelect'
                  style={{ width: '100%' }}
                  placeholder='Select UGC Level'
                  value={validation.values.ugc_level || undefined}
                  onChange={value => validation.setFieldValue('ugc_level', value)}
                  options={[
                    { label: 'Source', value: 'source' },
                    { label: 'Episode', value: 'episode' }
                  ]}
                />
              </div>
            )}
            {reportId === 24 && (
              <AssigneeDropdown
                value={validation.values.operator_id || undefined}
                onChange={value => validation.setFieldValue('operator_id', value)}
                className='customSelect'
                label='Operator'
              />
            )}
            {[11].includes(reportId) && (
              <div className='mb-3'>
                <DomainDropdown
                  onChange={(value, option) => {
                    validation.setFieldValue('domain_id', value)
                    setDomainName(option.label.replaceAll(' ', '_'))
                  }}
                  className='customSelect'
                  onBlur={validation.handleBlur}
                  value={validation.values.domain}
                  validation={validation}
                  isAll
                />
                {validation.touched.domain && validation.errors.domain ? (
                  <p className='text-danger'>{validation.errors.domain}</p>
                ) : null}
              </div>
            )}

            {/* Date fields */}
            {reportId && ![3, 7, 8, 10].includes(reportId) && (
              <div>
                <DateField
                  label='Start Date'
                  name='start_date'
                  validation={validation}
                />
                <DateField
                  label='End Date'
                  name='end_date'
                  validation={validation}
                />
              </div>
            )}
          </form>
        </CardBody>
        <CardFooter>
          {can('Generate_Report') && (
            <button
              type='submit'
              className='btn btn-outline-theme'
              onClick={async () => {
                const errors = await validation.validateForm()
                if (Object.keys(errors).length > 0) {
                  Object.values(errors).forEach(err => {
                    toast.error(err)
                  })

                  validation.setTouched(
                    Object.keys(errors).reduce((acc, key) => {
                      acc[key] = true
                      return acc
                    }, {})
                  )
                  return
                }

                validation.handleSubmit()
              }}
              disabled={isLoading}
            >
              Submit Report
            </button>
          )}
        </CardFooter>
      </Card>

      {/* Report Requests Table */}
      <Card className='mt-3'>
        <CardHeader className='d-flex justify-content-between align-items-center'>
          <span>Reports List</span>
          <button
            className='btn btn-sm btn-outline-theme'
            onClick={() => {
              refetch()
              toast.info('Refreshing report requests...')
            }}
            disabled={isLoadingRequests}
          >
            <i className='fa fa-refresh me-1'></i>
            Refresh
          </button>
        </CardHeader>
        <CardBody>
          <ReusableTable
            columns={reportRequestColumns}
            data={reportRequests?.data?.results || []}
            loading={isLoadingRequests || isFetching}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: reportRequests?.data?.count,
              showSizeChanger: true
            }}
            onChange={handleTableChange}
          />
        </CardBody>
      </Card>
    </>
  )
})

export default GenerateReports
